/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';
import {
  AuditFiltersToggleActiveFiltersAction,
  AuditFiltersToggleSelectedFiltersAction,
  AuditFiltersFetchAction,
  AuditFiltersFetchSuccessAction,
  AuditAllFilters,
  AuditTrialsListState,
  AuditFiltersSearchAction,
  AuditFiltersActionBySearchAction,
  AuditFiltersEventSearchAction,
  AuditBusinessProcessesFetchSuccessAction,
} from './auditFilters.types';

import { createSliceHook, makeActionCreator } from '../helpers';
import { RootState } from '../store';
import { toggleFilterState } from '../usersFilters/toggleFilterState';
import { auditTrailsFormatter } from './auditTrailsFormatter';
import { toBusinessProcessOption } from './businessProcess.helpers';

export type FilterDataType = Required<AuditAllFilters>;

const initialFilters: FilterDataType = {
  dateAndTime: [],
  actionBy: [],
  subjectId: [],
  visit: [],
  // Populated per clinical trial from the business-processes endpoint.
  businessProcess: [],
  event: [],
  actionType: [
    { key: 'CREATE', value: 'CREATE', label: 'Create' },
    { key: 'READ', value: 'READ', label: 'Read' },
    { key: 'UPDATE', value: 'UPDATE', label: 'Update' },
    { key: 'DELETE', value: 'DELETE', label: 'Delete' },
  ],
  field: [],
  newValue: [],
  reasonForModification: [],
};

const filters = {
  dateAndTime: [],
  actionBy: [],
  subjectId: [],
  visit: [],
  businessProcess: [],
  event: [],
  actionType: [],
  field: [],
  newValue: [],
  reasonForModification: [],
};

const initialState: AuditTrialsListState = {
  data: {},
  state: {
    // TODO: uncomment is when back will be ready to accept another filters
    actionBy: {
      active: false,
      label: 'Action by',
    },
    // dateAndTime: {
    //   active: false,
    //   label: 'Date & Time',
    // },
    // subjectId: {
    //   active: false,
    //   label: 'Subject ID',
    // },
    businessProcess: {
      active: false,
      label: 'Business Process',
    },
    actionType: {
      active: false,
      label: 'Action Type',
    },
    // TODO: uncomment is when back will be ready to accept another filters
    // visit: {
    //   active: false,
    //   label: 'Visit',
    // },
    event: {
      active: false,
      label: 'Event',
    },
    // field: {
    //   active: false,
    //   label: 'Field',
    // },
    // newValue: {
    //   active: false,
    //   label: 'New Values',
    // },
    // reasonForModification: {
    //   active: false,
    //   label: 'Reason For Modification',
    // },
  },
  allFilters: filters,
  selectedFilters: filters,
  associatedColumnsValues: [],
  loading: false,
  search: '',
  actionBySearch: '',
  eventSearch: '',
};

const { act, actReject, actReq, actResolve, actEmpty } =
  makeActionCreator<AuditTrialsListState>();

// TODO: rewrite to appropriate way
export const auditTrialsSlice = createSlice({
  name: 'auditFilters',
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },

    fetchAuditTrailList: actReq<AuditFiltersFetchAction['payload']>(),

    fetchAuditTrialListSuccess: act<AuditFiltersFetchSuccessAction['payload']>(
      (audits, state) => {
        const formatted = auditTrailsFormatter(audits, initialFilters);

        return {
          ...formatted,
          allFilters: {
            ...formatted.allFilters,
            businessProcess: state.allFilters.businessProcess,
          },
        };
      }
    ),

    fetchAuditTrialListFailure: actReject(),

    fetchBusinessProcesses: actReq<{ clinicalTrialId: string }>(null),

    fetchBusinessProcessesSuccess: act<
      AuditBusinessProcessesFetchSuccessAction['payload']
    >((codes, state) => ({
      allFilters: {
        ...state.allFilters,
        businessProcess: (codes ?? []).map(toBusinessProcessOption),
      },
    })),

    fetchBusinessProcessesFailure: actReject(),

    clear: filterSlice => ({ ...filterSlice, data: {} }),

    toggleActiveFilter: (
      stateSlice,
      action: AuditFiltersToggleActiveFiltersAction
    ) => toggleFilterState(stateSlice, action.payload),

    toggleSelectedFilter: act<
      AuditFiltersToggleSelectedFiltersAction['payload']
    >(({ name, value }, { selectedFilters }) => {
      const currentValues = selectedFilters[name] ?? [];
      const isSelected = currentValues.includes(value);

      // Business Process is multi-select; other filters stay single-select.
      if (name === 'businessProcess') {
        const nextValues = isSelected
          ? currentValues.filter(item => item !== value)
          : [...currentValues, value];

        return {
          selectedFilters: { ...selectedFilters, [name]: nextValues },
        };
      }

      return {
        selectedFilters: {
          ...selectedFilters,
          [name]: isSelected ? [] : [value],
        },
      };
    }),

    setSearch: act<AuditFiltersSearchAction['payload']>(search => ({ search })),

    setActionBySearch: act<AuditFiltersActionBySearchAction['payload']>(
      searchByActonString => ({
        actionBySearch: searchByActonString,
      })
    ),

    setEventSearch: act<AuditFiltersEventSearchAction['payload']>(
      searchByEventString => ({
        eventSearch: searchByEventString,
      })
    ),
  },
});

export const {
  reset,
  fetchAuditTrailList,
  fetchAuditTrialListSuccess,
  fetchAuditTrialListFailure,
} = auditTrialsSlice.actions;

export const actions = {
  ...auditTrialsSlice.actions,
};

export const useAuditTrialsFiltersSlice = createSliceHook(
  auditTrialsSlice.name,
  actions
);

// TODO: add auditTrialsto root
export const selectAuditFiltersState = (root: RootState) => root.auditFilters;
